// @ts-nocheck
import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLocation, useNavigate, Outlet, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/slice/authSlice';

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 72;

export default function ClientPanelLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [followUpsOpen, setFollowUpsOpen] = useState(true);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNotif, setAnchorElNotif] = useState<null | HTMLElement>(null);

  const authState = useSelector((state: RootState) => state.auth);
  const userName = authState.userName || 'Verified Buyer';

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    localStorage.removeItem('buyer_token');
    sessionStorage.removeItem('buyer_token');
    sessionStorage.removeItem('buyer');
    sessionStorage.removeItem('buyer_user');
    const domain = window.location.hostname.includes('sourceseas.com') ? '.sourceseas.com' : window.location.hostname;
    document.cookie = `buyer_token=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    dispatch(logout());
    navigate('/login');
  };

  const isRouteActive = (path: string) => {
    if (path === '/dashboard' && (location.pathname === '/' || location.pathname === '/dashboard')) {
      return true;
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#FFFFFF' }}>
      {/* Brand Header */}
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          px: 2,
          borderBottom: '1px solid #E8DFD5',
        }}
      >
        {(!collapsed || isMobile) && (
          <Box
            component={RouterLink}
            to="/order-tracker"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Sourceseas"
              onError={(e: any) => {
                e.target.style.display = 'none';
              }}
              sx={{ width: 34, height: 34, objectFit: 'contain' }}
            />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#5F4B3B', lineHeight: 1.1 }}>
                SOURCESEAS
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#A77B58', fontWeight: 700, letterSpacing: '0.8px' }}>
                CLIENT PANEL
              </Typography>
            </Box>
          </Box>
        )}

        {collapsed && !isMobile && (
          <Box
            component="img"
            src="/logo.png"
            alt="Logo"
            onError={(e: any) => {
              e.target.style.display = 'none';
            }}
            sx={{ width: 32, height: 32 }}
          />
        )}

        {!isMobile && (
          <IconButton size="small" onClick={() => setCollapsed(!collapsed)} sx={{ color: '#5F4B3B' }}>
            <ChevronLeftIcon sx={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
          </IconButton>
        )}
      </Box>

      {/* User Badge preview */}
      {(!collapsed || isMobile) && (
        <Box sx={{ p: 2, bgcolor: '#FAF6F0', borderBottom: '1px solid #E8DFD5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#A77B58', color: '#fff', width: 38, height: 38, fontWeight: 700 }}>
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: '13px', color: '#3B3027' }}>
                {userName}
              </Typography>
              <Chip
                label="Verified Client"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '10px',
                  fontWeight: 600,
                  bgcolor: 'rgba(16, 185, 129, 0.12)',
                  color: '#059669',
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation Menu (Matches sketch precisely) */}
      <List sx={{ px: 1, py: 2, flex: 1 }}>
        {/* 1. Dashboard (MM) */}
        <Tooltip title={collapsed ? 'Dashboard (Main Menu)' : ''} placement="right">
          <ListItem disablePadding sx={{ mb: 0.8 }}>
            <ListItemButton
              component={RouterLink}
              to="/dashboard"
              selected={isRouteActive('/dashboard')}
              sx={{
                borderRadius: '8px',
                py: 1.1,
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#A77B58',
                  color: '#FFFFFF',
                  '& .MuiListItemIcon-root': { color: '#FFFFFF' },
                  '&:hover': { bgcolor: '#936948' },
                },
                '&:hover': {
                  bgcolor: 'rgba(167, 123, 88, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isRouteActive('/dashboard') ? '#FFFFFF' : '#756B63' }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <ListItemText
                  primary="Dashboard"
                  secondary="Main Menu"
                  primaryTypographyProps={{ fontSize: '13.5px', fontWeight: 700 }}
                  secondaryTypographyProps={{ fontSize: '10px', color: isRouteActive('/dashboard') ? '#E8DFD5' : '#9E948C' }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/* 2. Follow-Ups (MM) with Sub-Menus */}
        <ListItem disablePadding sx={{ mb: 0.8, display: 'block' }}>
          <Tooltip title={collapsed ? 'Follow-Ups (Main Menu)' : ''} placement="right">
            <ListItemButton
              onClick={() => {
                if (collapsed && !isMobile) setCollapsed(false);
                setFollowUpsOpen(!followUpsOpen);
              }}
              sx={{
                borderRadius: '8px',
                py: 1.1,
                px: 1.5,
                bgcolor: location.pathname.includes('/follow-ups') ? 'rgba(167, 123, 88, 0.08)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(167, 123, 88, 0.12)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#756B63' }}>
                <AssignmentIcon fontSize="small" />
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <>
                  <ListItemText
                    primary="Follow-Ups"
                    secondary="Main Menu"
                    primaryTypographyProps={{ fontSize: '13.5px', fontWeight: 700, color: '#3B3027' }}
                    secondaryTypographyProps={{ fontSize: '10px', color: '#9E948C' }}
                  />
                  {followUpsOpen ? (
                    <ExpandLess sx={{ color: '#756B63', fontSize: 18 }} />
                  ) : (
                    <ExpandMore sx={{ color: '#756B63', fontSize: 18 }} />
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>

          {/* Sub Menu Items: Inquiries (SM), REQ-samples (SM), Quotations (SM) */}
          <Collapse in={followUpsOpen && (!collapsed || isMobile)} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2, mt: 0.5 }}>
              {/* Inquiries (SM) */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/follow-ups/inquiries"
                  selected={isRouteActive('/follow-ups/inquiries')}
                  sx={{
                    borderRadius: '6px',
                    py: 0.8,
                    pl: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(167, 123, 88, 0.18)',
                      color: '#5F4B3B',
                      fontWeight: 700,
                      '& .MuiListItemIcon-root': { color: '#A77B58' },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(167, 123, 88, 0.06)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: '#756B63' }}>
                    <QuestionAnswerIcon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Inquiries"
                    secondary="Sub Menu"
                    primaryTypographyProps={{ fontSize: '12.5px', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '9.5px', color: '#9E948C' }}
                  />
                  <Chip label="3" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#E8D8C1', color: '#5F4B3B' }} />
                </ListItemButton>
              </ListItem>

              {/* REQ-samples (SM) */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/follow-ups/req-samples"
                  selected={isRouteActive('/follow-ups/req-samples')}
                  sx={{
                    borderRadius: '6px',
                    py: 0.8,
                    pl: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(167, 123, 88, 0.18)',
                      color: '#5F4B3B',
                      fontWeight: 700,
                      '& .MuiListItemIcon-root': { color: '#A77B58' },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(167, 123, 88, 0.06)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: '#756B63' }}>
                    <Inventory2Icon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="REQ-samples"
                    secondary="Sub Menu"
                    primaryTypographyProps={{ fontSize: '12.5px', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '9.5px', color: '#9E948C' }}
                  />
                  <Chip label="3" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#E8D8C1', color: '#5F4B3B' }} />
                </ListItemButton>
              </ListItem>

              {/* Quotations (SM) */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/follow-ups/quotations"
                  selected={isRouteActive('/follow-ups/quotations')}
                  sx={{
                    borderRadius: '6px',
                    py: 0.8,
                    pl: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(167, 123, 88, 0.18)',
                      color: '#5F4B3B',
                      fontWeight: 700,
                      '& .MuiListItemIcon-root': { color: '#A77B58' },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(167, 123, 88, 0.06)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: '#756B63' }}>
                    <RequestQuoteIcon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Quotations"
                    secondary="Sub Menu"
                    primaryTypographyProps={{ fontSize: '12.5px', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '9.5px', color: '#9E948C' }}
                  />
                  <Chip label="3" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#E8D8C1', color: '#5F4B3B' }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Collapse>
        </ListItem>

        {/* 3. Order Tracker (MM) - Highlighted as in the user's diagram */}
        <Tooltip title={collapsed ? 'Order Tracker (Main Menu)' : ''} placement="right">
          <ListItem disablePadding sx={{ mb: 0.8 }}>
            <ListItemButton
              component={RouterLink}
              to="/order-tracker"
              selected={isRouteActive('/order-tracker')}
              sx={{
                borderRadius: '8px',
                py: 1.1,
                px: 1.5,
                border: isRouteActive('/order-tracker') ? 'none' : '1px solid rgba(167, 123, 88, 0.25)',
                '&.Mui-selected': {
                  bgcolor: '#A77B58',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(167, 123, 88, 0.35)',
                  '& .MuiListItemIcon-root': { color: '#FFFFFF' },
                  '&:hover': { bgcolor: '#936948' },
                },
                '&:hover': {
                  bgcolor: 'rgba(167, 123, 88, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isRouteActive('/order-tracker') ? '#FFFFFF' : '#A77B58' }}>
                <LocalShippingIcon fontSize="small" />
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <>
                  <ListItemText
                    primary="Order Tracker"
                    secondary="Main Menu (Active)"
                    primaryTypographyProps={{ fontSize: '13.5px', fontWeight: 800 }}
                    secondaryTypographyProps={{ fontSize: '10px', color: isRouteActive('/order-tracker') ? '#E8DFD5' : '#756B63' }}
                  />
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '9.5px',
                      fontWeight: 700,
                      bgcolor: isRouteActive('/order-tracker') ? '#FFFFFF' : '#10b981',
                      color: isRouteActive('/order-tracker') ? '#A77B58' : '#FFFFFF',
                    }}
                  />
                </>
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>

      <Divider sx={{ borderColor: '#E8DFD5' }} />

      {/* Bottom Profile & Actions */}
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          component={RouterLink}
          to="/profile"
          selected={isRouteActive('/profile')}
          sx={{
            borderRadius: '8px',
            py: 0.8,
            px: 1,
            '&:hover': { bgcolor: 'rgba(167, 123, 88, 0.08)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#756B63' }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {(!collapsed || isMobile) && (
            <ListItemText primary="My Account Profile" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
          )}
        </ListItemButton>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '8px',
            py: 0.8,
            px: 1,
            color: '#c2410c',
            '&:hover': { bgcolor: '#fff7ed' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#c2410c' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {(!collapsed || isMobile) && (
            <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F5F0EB' }}>
      {/* Top Application Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: {
            md: `calc(100% - ${collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
          },
          ml: {
            md: `${collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px`,
          },
          bgcolor: '#FFFFFF',
          color: '#3B3027',
          borderBottom: '1px solid #E8DFD5',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, color: '#5F4B3B' }}
            >
              <MenuIcon />
            </IconButton>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027', fontSize: { xs: '15px', md: '18px' } }}>
                Client Post-Login Portal
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#756B63', display: { xs: 'none', sm: 'block' } }}>
                Trade Expert • Order Tracking & Supplier Connect
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Quick Supplier Connect Alert Indicator */}
            <Tooltip title="Supplier Connect Alerts Active">
              <Chip
                icon={<WarningAmberIcon style={{ color: '#d97706', fontSize: 16 }} />}
                label="3 Supplier Alerts"
                size="small"
                component={RouterLink}
                to="/order-tracker"
                clickable
                sx={{
                  bgcolor: '#fef3c7',
                  color: '#92400e',
                  fontWeight: 700,
                  fontSize: '11px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  border: '1px solid #fde68a',
                }}
              />
            </Tooltip>

            {/* Notification Bell */}
            <IconButton
              size="medium"
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorElNotif(e.currentTarget)}
              sx={{ color: '#5F4B3B' }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Notification Popover */}
            <Menu
              anchorEl={anchorElNotif}
              open={Boolean(anchorElNotif)}
              onClose={() => setAnchorElNotif(null)}
              PaperProps={{ sx: { width: 320, p: 1, borderRadius: 2, border: '1px solid #E8DFD5' } }}
            >
              <Typography sx={{ px: 1.5, py: 0.5, fontWeight: 700, fontSize: '13px', color: '#3B3027' }}>
                Supplier Connect Alerts & Updates
              </Typography>
              <Divider sx={{ my: 1 }} />
              <MenuItem
                onClick={() => {
                  setAnchorElNotif(null);
                  navigate('/order-tracker');
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#d97706' }}>
                    ⚠️ Fall Short: Material
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#756B63' }}>
                    Order #ORD-2026-8491 - Supplier reported raw glaze shortage.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorElNotif(null);
                  navigate('/order-tracker');
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#b45309' }}>
                    ⏳ Availability: Logistical
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#756B63' }}>
                    Order #ORD-2026-8493 - Dry port slot congestion reported.
                  </Typography>
                </Box>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ textAlign: 'center', py: 0.5 }}>
                <Typography
                  component={RouterLink}
                  to="/order-tracker"
                  onClick={() => setAnchorElNotif(null)}
                  sx={{ fontSize: '12px', fontWeight: 700, color: '#A77B58', textDecoration: 'none' }}
                >
                  View All Orders in Order Tracker &rarr;
                </Typography>
              </Box>
            </Menu>

            {/* User Profile Avatar and Menu */}
            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  bgcolor: '#A77B58',
                  color: '#FFFFFF',
                  width: 36,
                  height: 36,
                  fontSize: '14px',
                  fontWeight: 700,
                  border: '2px solid #E8D8C1',
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleUserMenuClose}
              PaperProps={{ sx: { minWidth: 200, borderRadius: 2, border: '1px solid #E8DFD5' } }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E8DFD5' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#3B3027' }}>{userName}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>Client Account</Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/profile');
                }}
              >
                <PersonIcon fontSize="small" sx={{ mr: 1, color: '#756B63' }} />
                Profile Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/order-tracker');
                }}
              >
                <LocalShippingIcon fontSize="small" sx={{ mr: 1, color: '#A77B58' }} />
                Order Tracker
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: '#c2410c' }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1, color: '#c2410c' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Left Sidebar Drawer - Desktop & Mobile */}
      <Box
        component="nav"
        sx={{
          width: { md: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile Temporary Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid #E8DFD5',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Permanent Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
              borderRight: '1px solid #E8DFD5',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: {
            xs: '100%',
            md: `calc(100% - ${collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
          },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          bgcolor: '#FAF8F5',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
